import type { ReactNode } from "react";
import { AlertTriangle, CircleCheck, Clock } from "lucide-react";
import { plural } from "@amber/contracts";
import { AppLink, PopoverHeading, StatusPopover, cx, formatCount, formatTime } from "@amber/ui";
import type { HomeResponse } from "@amber/application/contracts";

type Tone = HomeResponse["status"]["tone"];

const TONE_TEXT: Record<Tone, string> = {
  disrupted: "text-attention",
  with_issues: "text-waiting",
  normal: "text-done",
};
const TONE_ICON: Record<Tone, typeof CircleCheck> = {
  normal: CircleCheck,
  disrupted: AlertTriangle,
  with_issues: Clock,
};
const TONE_BORDER: Record<Tone, string> = {
  disrupted: "border-l-attention",
  with_issues: "border-l-waiting",
  normal: "",
};

function statusParts(status: HomeResponse["status"], team: boolean): ReactNode[] {
  return [
    ...status.issues.map((i) =>
      team ? (
        <span key={i.href + i.text}>{i.text}</span>
      ) : (
        <AppLink key={i.href + i.text} href={i.href} className="text-fg underline decoration-1 underline-offset-2">
          {i.text}
        </AppLink>
      ),
    ),
    ...status.pauses.map((p) =>
      team ? (
        <span key={p.text}>{p.text}</span>
      ) : (
        <AppLink key={p.text} href={p.href} className="text-fg underline decoration-1 underline-offset-2">
          {p.text}
        </AppLink>
      ),
    ),
  ];
}

function StatusRules({ rules }: { rules: HomeResponse["rules"] }) {
  return (
    <StatusPopover label="How this is written">
      <PopoverHeading>How this is written</PopoverHeading>
      <p>Checked in this order, with the values set in Settings › Checks and policies:</p>
      <p>
        <b>Disrupted</b> when, for {plural(rules.disrupted_hours, "hour", { format: formatCount })}, more than half of
        the scans that were due have not run, or items wait to be downloaded or archived and none has finished; or a
        required route is failing; or {formatCount(rules.disrupted_issues)} or more issues at once.
      </p>
      <p>
        <b>Running, with issues</b> from {plural(rules.with_issues, "issue", { format: formatCount })}: a platform-wide
        cause, a failing or partial source, or a late required route.
      </p>
      <p>
        <b>Running normally</b> otherwise. Pauses are always named but are not issues. The sentence never claims that
        collection is complete.
      </p>
    </StatusPopover>
  );
}

export function StatusPanel({ data, team }: { data: HomeResponse; team: boolean }) {
  const { status } = data;
  const tone = TONE_TEXT[status.tone];
  const Icon = TONE_ICON[status.tone];
  const parts = statusParts(status, team);
  return (
    <section
      aria-labelledby="home-status"
      className={cx(
        "rounded-xl border border-divider bg-bg px-4 py-3",
        status.tone !== "normal" && "border-l-4",
        TONE_BORDER[status.tone],
      )}
    >
      <h2 id="home-status" className="sr-only">
        Status
      </h2>
      <p className={cx("m-0 flex flex-wrap items-baseline gap-x-1.5", team ? "text-[28px]" : "text-xl")}>
        <Icon
          size={team ? 26 : 17}
          strokeWidth={1.8}
          aria-hidden="true"
          className={cx("flex-none self-center", tone)}
        />
        <b className={cx("font-semibold", tone)}>
          {status.label}
          {parts.length > 0 ? ":" : "."}
        </b>
        {parts.map((p, i) => (
          <span key={i}>
            {p}
            {i < parts.length - 1 ? " · " : "."}
          </span>
        ))}
      </p>
      {status.disrupted_because.length > 0 ? (
        <p className="m-0 mt-1 text-md text-attention">{status.disrupted_because.join(" · ")}</p>
      ) : null}
      <p className="m-0 mt-1 flex items-center gap-1.5 text-md text-muted">
        Updated {formatTime(data.generated_at)} · from scan results and pipeline status
        {team ? null : (
          <>
            {" · "}
            <StatusRules rules={data.rules} />
          </>
        )}
      </p>
    </section>
  );
}
